/**
 * 打包 App 里安全地创建带文件名的 File/Blob。
 *
 * 背景：cordova-plugin-file（本项目 local-plugins 内置 8.1.0）会把 window.File
 * clobber 成它自己的旧式实现，构造签名是
 *   File(name, localURL, type, lastModifiedDate, size)
 * 而不是浏览器标准的
 *   File(parts, name, options)
 * 因此在打包 App 里直接 `new File([blob], name, { type })` 会得到畸形对象：
 * 属性 name 变成 Blob 数组、type 变成 options 对象——上传文件名丢失（落兜底名），
 * 任何 `.name.split(...)` 都会抛 "Cannot read properties of undefined (reading 'split')"。
 *
 * 原生 File 构造函数保存在 cordova modulemapper 的 CDV_origSymbols 里
 * （插件自己的 FileReader 也靠它找回原生实现），此处取回并探测其可用性；
 * 若取不到可用构造（浏览器无插件 / 个别平台未存原始符号），
 * 回落为 window.Blob + 手动挂 name 属性——FormData.append(blob, filename)
 * 与后续读 .name/.size/.type 均不受影响。
 */

/** 缓存解析结果（undefined = 尚未解析） */
let cachedNativeFileCtor: typeof File | undefined;

/** 解析原生 File 构造函数；解析失败返回 window.File（可能是被替换的实现，调用方再探测） */
function resolveNativeFileCtor(): typeof File {
  if (cachedNativeFileCtor !== undefined) return cachedNativeFileCtor;
  const candidates: unknown[] = [];
  try {
    const w = window as unknown as {
      cordova?: { require?: (id: string) => unknown };
    };
    if (typeof w.cordova?.require === "function") {
      const mapper = w.cordova.require("cordova/modulemapper") as {
        getOriginalSymbol?: (ctx: unknown, path: string) => unknown;
      };
      const orig =
        typeof mapper?.getOriginalSymbol === "function"
          ? mapper.getOriginalSymbol(window, "File")
          : undefined;
      if (typeof orig === "function") candidates.push(orig);
    }
  } catch {
    /* 浏览器等无 cordova 环境：直接走 window.File */
  }
  if (typeof File === "function") candidates.push(File);

  for (const c of candidates) {
    try {
      const probe = new (c as typeof File)(
        [new Blob(["probe"])],
        "__name_probe__.txt",
        { type: "text/plain" },
      );
      // 标准实现：name 是传入的字符串；被替换的旧式实现：name 会变成 Blob 数组
      if (
        probe &&
        typeof (probe as { name?: unknown }).name === "string" &&
        (probe as { name?: unknown }).name === "__name_probe__.txt"
      ) {
        cachedNativeFileCtor = c as typeof File;
        return cachedNativeFileCtor;
      }
    } catch {
      /* 该候选不可用，试下一个 */
    }
  }
  cachedNativeFileCtor = File;
  return cachedNativeFileCtor;
}

/**
 * 创建带文件名的 File（返回类型按 File 声明；极端回落时是带 name 属性的 Blob，
 * 调用方只允许读 name/size/type，行为一致）。
 */
export function createNativeFile(
  parts: BlobPart[],
  name: string,
  options: { type?: string } = {},
): File {
  const ctor = resolveNativeFileCtor();
  try {
    const f = new ctor(parts, name, options);
    if (f && typeof f.name === "string" && f.name === name) {
      return f;
    }
  } catch {
    /* 回落 */
  }
  // 最后一个兜底：window.Blob 没有被插件动过；手动挂上 name 属性
  const blob = new Blob(parts, options);
  try {
    Object.defineProperty(blob, "name", {
      value: name,
      configurable: true,
    });
  } catch {
    /* 极老内核不允许扩展 Blob：仅能拿到无名字 Blob，由上传侧兜底命名 */
  }
  return blob as unknown as File;
}
